// src/pages/HrMenteesPage.js
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '../components/ui/dialog';
import { useTranslation } from 'react-i18next';
import ChatPopup from '../components/ChatPopup';
import { MENTORING_API } from '../axiosInstance';

// Vorkonfigurierte Clients (wie im Dashboard)
import { authApi, mentoringApi as api } from '../axiosInstance';
import { ensureMentoringSession } from '../lib/session';

/* ========================= Projektfilter Sync ========================= */

// Muss exakt zum Key aus HrOverviewPage passen:
const DASHBOARD_PROJECT_FILTER_KEY = 'hr_overview_project_filter_v1';

function readDashboardProjectFilter() {
  try {
    return localStorage.getItem(DASHBOARD_PROJECT_FILTER_KEY) || '';
  } catch {
    return '';
  }
}

function writeDashboardProjectFilter(v) {
  try {
    if (v) localStorage.setItem(DASHBOARD_PROJECT_FILTER_KEY, String(v));
    else localStorage.removeItem(DASHBOARD_PROJECT_FILTER_KEY);
  } catch {}
}

function readSurveyIdFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search || '');
    // Primär: surveyId (wie wir es aus dem Overview übergeben)
    const a = params.get('surveyId');
    if (a && String(a).trim()) return String(a).trim();
    // Fallbacks, falls du später anders benennst
    const b = params.get('survey_id');
    if (b && String(b).trim()) return String(b).trim();
    const c = params.get('projectId');
    if (c && String(c).trim()) return String(c).trim();
  } catch {}
  return '';
}

/* ========================= Avatar-Helpers ========================= */

/** Kandidaten-URLs für einen Avatar bauen (robust gegen unterschiedliche Backends). */
function buildAvatarUrls(user) {
  const urls = [];

  // 1) Falls bereits ein vollständiger oder data:-String im Feld avatar
  if (user?.avatar && typeof user.avatar === 'string') {
    const a = user.avatar.trim();
    if (a.startsWith('data:') || a.startsWith('http')) {
      urls.push(a);
    }
  }

  // 2) Dateiname aus avatar_path / avatarPath
  const file = (user?.avatar_path || user?.avatarPath || '').toString().trim();
  if (file) {
    const apiBase = (MENTORING_API || '').replace(/\/$/, '');
    // häufigste Varianten (je nach Deployment)
    urls.push(`${apiBase}/uploads/${file}`); // z.B. https://app.menvio.ai/mentoring/uploads/xyz.png
    urls.push(`/mentoring/uploads/${file}`); // absolut, falls apiBase anders ist
    urls.push(`/uploads/${file}`); // un-namespaced (ältere Setups)
  }

  // 3) Platzhalter ganz zum Schluss
  urls.push(
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII='
  );
  return urls;
}

/** Avatar-IMG, springt bei onError automatisch zum nächsten Kandidaten. */
function AvatarImg({ user, className = 'w-16 h-16 rounded-full object-cover border border-gray-600' }) {
  const candidates = buildAvatarUrls(user);
  const [idx, setIdx] = useState(0);
  const src = candidates[Math.min(idx, candidates.length - 1)];
  return (
    <img
      src={src}
      alt="Avatar"
      className={className}
      loading="lazy"
      onError={() => setIdx((i) => Math.min(i + 1, candidates.length - 1))}
    />
  );
}

/* ========================= CSRF / Cookie Helper ========================= */

function getCookie(name) {
  const value = document.cookie.split('; ').find((row) => row.startsWith(name + '='))?.split('=')[1];
  return value ? decodeURIComponent(value) : null;
}

/* ========================= Meta/Score Helpers ========================= */

const pickMeta = (p) => p?.active_meta || p?.current_matching || null;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '–');

function pickScore(obj) {
  if (!obj || typeof obj !== 'object') return null;

  const direct = [obj.score, obj.overall_score, obj.total_score, obj.matching_score, obj.match_score, obj.score_total];
  for (const v of direct) {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }

  const fach = Number(obj.fachlicher_score);
  const pers = Number(obj.persoenlicher_score ?? obj['persönlicher_score']);
  const fachOk = !Number.isNaN(fach);
  const persOk = !Number.isNaN(pers);
  if (fachOk && persOk) return Number((fach + pers).toFixed(2));
  if (fachOk) return fach;
  if (persOk) return pers;

  const nested = obj.matching ?? obj.active_matching ?? obj.match ?? obj.scores ?? null;
  if (nested && typeof nested === 'object') {
    const n = pickScore(nested);
    if (n != null) return n;
  }

  return null;
}

function getRowScore(row) {
  const meta = pickMeta(row);
  const fromMeta = pickScore(meta);
  if (fromMeta != null) return fromMeta;

  const fromRoot = pickScore(row);
  if (fromRoot != null) return fromRoot;

  const fromAlt = pickScore(row.matching) ?? pickScore(row.active_matching) ?? pickScore(row.match) ?? null;
  if (fromAlt != null) return fromAlt;

  return null;
}

const fmtScore = (row) => {
  const s = getRowScore(row);
  return s == null ? '–' : s;
};

/* ==================== Matching-Status-Helper ==================== */

function toLowerSafe(v) {
  return v == null ? '' : String(v).trim().toLowerCase();
}

function truthyFlag(v) {
  if (v === true) return true;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') {
    const s = toLowerSafe(v);
    if (!s) return false;
    // typische Truthy-Werte
    return ['1', 't', 'true', 'yes', 'y', 'ja', 'matched', 'active'].includes(s);
  }
  return false;
}

/**
 * Versucht, ein "aktives Matching" robust zu erkennen.
 * Deckt verschiedene Varianten ab:
 * - matched / has_active_matching Flags
 * - status: confirmed / active / matching_active / bestätigt / aktiv / …
 */
function isMatchingActive(row) {
  if (!row || typeof row !== 'object') return false;

  const meta = pickMeta(row) || {};

  const statusCandidates = [row.status, row.match_status, meta.status, meta.match_status]
    .filter((v) => v != null)
    .map(toLowerSafe);

  // klare positive Status-Werte
  if (statusCandidates.some((s) => ['confirmed', 'active', 'matching_active', 'matched', 'bestätigt', 'bestaetigt', 'aktiv'].includes(s))) {
    return true;
  }

  // generische Muster: enthält "confirm", "match" oder "aktiv", ohne klar negative Begriffe
  if (
    statusCandidates.some((s) => {
      if (!s) return false;
      if (s.includes('cancel') || s.includes('reject') || s.includes('ablehn') || s.includes('frei') || s.includes('open') || s.includes('pending')) {
        return false;
      }
      return s.includes('confirm') || s.includes('match') || s.includes('aktiv');
    })
  ) {
    return true;
  }

  // Bool-/Flag-Felder
  const flagCandidates = [row.matched, row.has_active_matching, row.is_matched, meta.matched, meta.has_active_matching, meta.is_matched];
  if (flagCandidates.some(truthyFlag)) return true;

  return false;
}

function extractCustomerId(who) {
  if (!who) return null;
  const src = who.data ?? who.user ?? who.profile ?? who.hr_profile ?? who;
  const candidates = [src.customer_id, src.customerId, src.tenant_id, src.tenantId, src.customer?.id, src.tenant?.id];
  for (const v of candidates) {
    const n = Number(v);
    if (Number.isInteger(n) && n > 0) return n;
  }
  return null;
}

/* =============================== Page ============================== */

export default function HrMenteesPage() {
  const [mentees, setMentees] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedMentee, setSelectedMentee] = useState(null);

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name'); // name | score | confirmed

  // Initial: URL ?surveyId=... > localStorage Dashboard > ''
  const [projectFilter, setProjectFilter] = useState(() => readSurveyIdFromUrl() || readDashboardProjectFilter() || '');

  const [allProjectIds, setAllProjectIds] = useState([]);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // HR-Chat-Zustand
  const [hrChatConfig, setHrChatConfig] = useState(null);

  const navigate = useNavigate();
  const { t } = useTranslation();

  // URL + localStorage synchron halten, wenn der Filter geändert wird
  function syncFilterToUrlAndStorage(next) {
    const v = next ? String(next) : '';
    writeDashboardProjectFilter(v);

    try {
      const params = new URLSearchParams(window.location.search || '');
      if (v) params.set('surveyId', v);
      else params.delete('surveyId');

      const qs = params.toString();
      const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      navigate(url, { replace: true });
    } catch {
      // not fatal
    }
  }

  useEffect(() => {
    let alive = true;

    // *** Mentoring-first, Auth nur wenn wirklich authentifiziert ***
    async function fetchWhoAmI() {
      // 1) Mentoring-WhoAmI
      try {
        const m = (await api.get('/whoami', { withCredentials: true, timeout: 5000 }))?.data;
        if (m?.user && (m.user.customer_id || m.user.client_id || m.user.email)) return m;
      } catch {}

      // 2) Auth-Profil
      try {
        const p = (await authApi.get('/profile', { withCredentials: true, timeout: 5000 }))?.data;
        if (p?.isAuthenticated && p?.user) return p;
      } catch {}

      try {
        const a = (await authApi.get('/whoami', { withCredentials: true, timeout: 5000 }))?.data;
        if (a?.isAuthenticated && a?.user) return a;
      } catch {}

      // 3) Tenant-Package
      try {
        const tp = (await api.get('/tenant-package', { withCredentials: true, timeout: 5000 }))?.data;
        if (tp?.ok && tp.customer_id && tp.email) {
          return {
            ok: true,
            user: { email: tp.email, role: 'hr', customer_id: tp.customer_id, client_id: tp.customer_id },
          };
        }
      } catch {}

      // 4) CSRF anstoßen und nochmal Mentoring-WhoAmI probieren
      try {
        await authApi.get('/csrf-token', { withCredentials: true, timeout: 5000 });
      } catch {}
      try {
        const m2 = (await api.get('/whoami', { withCredentials: true, timeout: 5000 }))?.data;
        if (m2?.user) return m2;
      } catch {}

      return null;
    }

    async function requestMentees(path) {
      // CSRF-Cookie sicherstellen
      try {
        await authApi.get('/csrf-token', { withCredentials: true, timeout: 4000 });
      } catch {}

      const xsrf = getCookie('XSRF-TOKEN');
      const headers = {
        'X-Requested-With': 'XMLHttpRequest',
        ...(xsrf
          ? {
              'X-CSRF-Token': xsrf,
              'X-XSRF-TOKEN': xsrf,
              'x-csrf-token': xsrf,
              'x-xsrf-token': xsrf,
            }
          : {}),
      };

      return api.get(path, {
        withCredentials: true,
        headers,
        timeout: 12000,
        xsrfCookieName: 'XSRF-TOKEN',
        xsrfHeaderName: 'X-CSRF-Token',
      });
    }

    async function load() {
      setLoading(true);
      setError('');
      try {
        await ensureMentoringSession();

        let who = await fetchWhoAmI();

        if (!who?.user) {
          try {
            who = (await api.get('/whoami', { withCredentials: true, timeout: 5000 }))?.data;
          } catch {}
        }

        const customerId = extractCustomerId(who);

        if (!alive) return;

        if (!Number.isInteger(customerId) || customerId <= 0) {
          // Tenant-Package direkt
          try {
            const tp = (await api.get('/tenant-package', { withCredentials: true, timeout: 5000 }))?.data;
            if (tp?.ok && tp.customer_id) {
              const cidTp = Number(tp.customer_id);
              if (Number.isInteger(cidTp) && cidTp > 0) {
                const surveyPartTp = projectFilter ? `&survey_id=${encodeURIComponent(projectFilter)}` : '';
                const pathTp = `/mentees/ready?customer_id=${encodeURIComponent(cidTp)}&sort=name_asc${surveyPartTp}`;
                const { data } = await requestMentees(pathTp);
                const rows = Array.isArray(data) ? data : [];
                const mapped = rows.map((r) => ({ ...r, avatarPath: r.avatar_path }));

                const uniqueSurveyIds = Array.from(
                  new Set(
                    mapped
                      .map((m) => {
                        const sid = m.survey_id ?? m.active_meta?.survey_id ?? m.current_matching?.survey_id ?? null;
                        return sid != null ? String(sid) : null;
                      })
                      .filter(Boolean)
                  )
                );

                setAllProjectIds(uniqueSurveyIds);
                setMentees(mapped);
                setFiltered(mapped);
                setLoading(false);
                return;
              }
            }
          } catch {}

          setMentees([]);
          setFiltered([]);
          setAllProjectIds([]);
          setError(t('errorLoading', 'Fehler beim Laden.'));
          setLoading(false);
          return;
        }

        const surveyPart = projectFilter ? `&survey_id=${encodeURIComponent(projectFilter)}` : '';
        const path = `/mentees/ready?customer_id=${encodeURIComponent(customerId)}&sort=name_asc${surveyPart}`;

        let data;
        try {
          ({ data } = await requestMentees(path));
        } catch (e) {
          if (e?.response?.status === 401 || e?.response?.status === 403) {
            try {
              await authApi.get('/csrf-token', { withCredentials: true, timeout: 4000 });
              ({ data } = await requestMentees(path));
            } catch (e2) {
              throw e2;
            }
          } else {
            throw e;
          }
        }

        const rows = Array.isArray(data) ? data : [];
        const mapped = rows.map((r) => ({ ...r, avatarPath: r.avatar_path }));

        const uniqueSurveyIds = Array.from(
          new Set(
            mapped
              .map((m) => {
                const sid = m.survey_id ?? m.active_meta?.survey_id ?? m.current_matching?.survey_id ?? null;
                return sid != null ? String(sid) : null;
              })
              .filter(Boolean)
          )
        );

        setAllProjectIds(uniqueSurveyIds);
        setMentees(mapped);
        setFiltered(mapped);
      } catch (e) {
        console.error('load mentees failed:', e);
        if (!alive) return;
        setError(t('errorLoading', 'Fehler beim Laden.'));
        setMentees([]);
        setFiltered([]);
        setAllProjectIds([]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [t, projectFilter]);

  // Suche/Filter/Sortierung clientseitig
  useEffect(() => {
    let results = [...mentees];

    if (search.trim()) {
      const s = search.toLowerCase();
      results = results.filter(
        (m) =>
          `${m.first_name || ''} ${m.last_name || ''}`.toLowerCase().includes(s) ||
          (m.abteilung || '').toLowerCase().includes(s) ||
          (m.job_title || '').toLowerCase().includes(s) ||
          (m.email || '').toLowerCase().includes(s)
      );
    }

    if (projectFilter) {
      results = results.filter((m) => {
        const sid = m.survey_id ?? m.active_meta?.survey_id ?? m.current_matching?.survey_id ?? null;
        return sid && String(sid) === String(projectFilter);
      });
    }

    if (sortBy === 'name') {
      results.sort((a, b) =>
        `${a.last_name || ''}${a.first_name || ''}`.localeCompare(`${b.last_name || ''}${b.first_name || ''}`)
      );
    } else if (sortBy === 'score') {
      results.sort((a, b) => {
        const sa = getRowScore(a) ?? 0;
        const sb = getRowScore(b) ?? 0;
        return sb - sa;
      });
    } else if (sortBy === 'confirmed') {
      results.sort((a, b) => {
        const da = pickMeta(a)?.confirmed_at ? new Date(pickMeta(a).confirmed_at).getTime() : 0;
        const db = pickMeta(b)?.confirmed_at ? new Date(pickMeta(b).confirmed_at).getTime() : 0;
        return db - da;
      });
    }

    setFiltered(results);
  }, [search, sortBy, projectFilter, mentees]);

  const hasAny = useMemo(() => filtered.length > 0, [filtered]);

  const projectOptions = useMemo(() => {
    const list = Array.isArray(allProjectIds) ? allProjectIds : [];
    const set = new Set(list.map(String));
    if (projectFilter && !set.has(String(projectFilter))) {
      return [String(projectFilter), ...list.map(String)];
    }
    return list.map(String);
  }, [allProjectIds, projectFilter]);

  function openHrChatForMentee(menteeObj) {
    const menteeId = Number(menteeObj?.user_id ?? menteeObj?.id) || null;
    setHrChatConfig({
      menteeId,
      mentorId: null,
      senderRole: 'hr',
      receiverRole: 'mentee',
      threadType: 'hr',
      presetMessage: '',
    });
  }

  return (
    <div className="min-h-screen bg-black text-white p-6 space-y-6">
      <button
        onClick={() => navigate('/hr/overview')}
        className="bg-green-400 text-black font-semibold px-4 py-2 rounded-full hover:bg-green-300 transition"
      >
        ← {t('backToHRDashboard', 'Zurück zum HR-Dashboard')}
      </button>

      <h1 className="text-3xl font-bold">{t('allMentees', 'Alle Mentees')}</h1>

      {/* Such-/Filter-/Sortierzeile */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <input
          type="text"
          placeholder={t('searchPlaceholder', 'Suche nach Name, Abteilung, Jobtitel')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-1/2 p-2 rounded bg-gray-800 text-white focus:outline-none"
        />

        <div className="flex flex-wrap items-center gap-3">
          <select
            value={projectFilter}
            onChange={(e) => {
              const v = e.target.value || '';
              setProjectFilter(v);
              syncFilterToUrlAndStorage(v);
            }}
            className="p-2 rounded bg-gray-800 text-white focus:outline-none"
          >
            <option value="">{t('allProjects', 'Alle Projekte')}</option>
            {projectOptions.map((id) => (
              <option value={id} key={id}>
                {t('projectId', 'Projekt-ID')}: {id}
              </option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="p-2 rounded bg-gray-800 text-white focus:outline-none"
          >
            <option value="name">{t('sortByName', 'Sortiere nach Name (A–Z)')}</option>
            <option value="score">{t('sortByScore', 'Sortiere nach Score')}</option>
            <option value="confirmed">{t('sortByConfirmed', 'Sortiere nach Bestätigt am')}</option>
          </select>
        </div>
      </div>

      {loading && <div className="text-gray-300">{t('loading', 'Lade…')}</div>}
      {error && <div className="text-red-400">{error}</div>}

      {!loading && !hasAny && !error && <div className="text-gray-300">{t('noMenteesFound', 'Keine Mentees gefunden.')}</div>}

      {/* Kartenansicht */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((mentee) => {
          const meta = pickMeta(mentee);
          const isActive = isMatchingActive(mentee);

          return (
            <div
              key={mentee.user_id ?? `${mentee.email}-${mentee.first_name}-${mentee.last_name}`}
              onClick={() => setSelectedMentee(mentee)}
              className="bg-gray-800 rounded-2xl p-6 flex items-center gap-4 shadow-md cursor-pointer hover:bg-gray-700"
            >
              <AvatarImg user={mentee} />

              <div className="flex-1">
                <div className="text-lg font-semibold flex items-center gap-2">
                  {mentee.first_name} {mentee.last_name}
                  {isActive && (
                    <span
                      title={t('matchingActive', 'Matching aktiv')}
                      className="inline-flex items-center justify-center ml-2 w-5 h-5 rounded-full bg-mentixxgreen text-black text-xs font-bold shadow-sm"
                    >
                      ✓
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-400">
                  {mentee.abteilung || '—'} · {mentee.job_title || '—'}
                </div>
                {(meta?.survey_id || mentee?.survey_id) && (
                  <div className="text-xs text-green-400 font-semibold mt-1">
                    {t('projectId', 'Projekt-ID')}: {meta?.survey_id ?? mentee?.survey_id}
                  </div>
                )}
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openHrChatForMentee(mentee);
                }}
                className="ml-2 px-3 py-1 rounded-full bg-mentixxgreen text-black text-sm font-semibold hover:bg-green-400"
                title={t('openChatWithMentee', 'Chat mit Mentee öffnen')}
              >
                {t('chat', 'Chat')}
              </button>
            </div>
          );
        })}
      </div>

      {/* Detail-Dialog */}
      {selectedMentee && (
        <Dialog open={true} onOpenChange={() => setSelectedMentee(null)}>
          <div className="bg-gray-900 text-white p-6 rounded-xl w-full max-w-md space-y-2">
            <div className="flex items-center gap-4">
              <AvatarImg user={selectedMentee} />
              <div>
                {(() => {
                  const meta = pickMeta(selectedMentee);
                  const isActive = isMatchingActive(selectedMentee);
                  return (
                    <>
                      <div className="text-xl font-semibold">
                        {selectedMentee.first_name} {selectedMentee.last_name}
                        {isActive && (
                          <span className="inline-flex items-center gap-1 ml-2 px-2.5 py-0.5 rounded-full bg-mentixxgreen text-black text-xs font-bold">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-800" />
                            <span>{t('matchingActive', 'Matching aktiv')}</span>
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-400">
                        {selectedMentee.abteilung || '—'} · {selectedMentee.job_title || '—'}
                      </div>
                      {(meta?.survey_id || selectedMentee?.survey_id) && (
                        <div className="text-xs text-green-400 font-semibold mt-1">
                          {t('projectId', 'Projekt-ID')}: {meta?.survey_id ?? selectedMentee?.survey_id}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="text-sm mt-4 space-y-1">
              <div>
                <strong>{t('email', 'E-Mail')}:</strong> {selectedMentee.email}
              </div>
              <div>
                <strong>{t('phone', 'Telefon')}:</strong> {selectedMentee.phone || '–'}
              </div>
              <div>
                <strong>{t('score', 'Score')}:</strong> {fmtScore(selectedMentee)}
              </div>
              <div>
                <strong>{t('status', 'Status')}:</strong> {pickMeta(selectedMentee)?.status ?? '–'}
              </div>
              <div>
                <strong>{t('confirmedAt', 'Bestätigt am')}:</strong> {fmtDate(pickMeta(selectedMentee)?.confirmed_at)}
              </div>
            </div>

            <div className="pt-3">
              <button
                onClick={() => openHrChatForMentee(selectedMentee)}
                className="px-4 py-2 rounded-full bg-mentixxgreen text-black font-bold hover:bg-green-400"
              >
                {t('openChatWithMentee', 'Chat mit Mentee öffnen')}
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {/* HR ↔︎ Mentee Chat */}
      {hrChatConfig && (
        <ChatPopup
          menteeId={hrChatConfig.menteeId}
          mentorId={null}
          senderRole={hrChatConfig.senderRole}
          receiverRole={hrChatConfig.receiverRole}
          threadType={hrChatConfig.threadType}
          presetMessage={hrChatConfig.presetMessage}
          onClose={() => setHrChatConfig(null)}
        />
      )}
    </div>
  );
}
